import { Modal, ModalDialog, ModalClose, ModalOverflow, Typography, Button, FormControl, FormLabel, Select, Option, Input, Textarea, Stack, Alert, CircularProgress, Box, Divider} from "@mui/joy";
import { motion, AnimatePresence } from "framer-motion";
import { useContext, useEffect, useState } from "react";
import { LayoutContext } from "../../context";
import { APIAssignTest, APIGetTests } from "../../api";
import { PersonRounded, AssignmentRounded, CalendarTodayRounded } from '@mui/icons-material';



export default function ModalAssignTest() {
    const { token, currentDoctor, patients, loadPatients, modalAssignTest, setModalAssignTest } = useContext(LayoutContext);
    
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    
    const [formData, setFormData] = useState({
        test_id: '',
        patient_id: '',
        comment: '',
        deadline: ''
    });
    
    const [tests, setTests] = useState([]);
    const [filteredPatients, setFilteredPatients] = useState([]);
    
    // Загрузка тестов при открытии модального окна
    useEffect(() => {
        if (modalAssignTest) {
            loadTests();
            filterPatients();
            loadPatients();
        }
    }, [modalAssignTest]);
    
    const loadTests = async () => {
        try {
            setLoading(true);
            const response = await APIGetTests(token, { is_active: true });
            if (response.success) {
                setTests(response.tests || []);
            }
        } catch (error) {
            console.error("Ошибка при загрузке тестов:", error);
            setError("Ошибка при загрузке тестов");
        } finally {
            setLoading(false);
        }
    };
    
    const filterPatients = () => {
        if (!currentDoctor) return;
        
        let filtered = patients;
        
        // Если пользователь не админ, фильтруем пациентов
        const isAdmin = (currentDoctor?.type_profile.includes('admin') || currentDoctor?.type_profile?.includes('admin')) || (currentDoctor?.type_profile.includes('supervisor') || currentDoctor?.type_profile?.includes('supervisor'));
        if (!isAdmin && currentDoctor?.id) {
            // Здесь нужно реализовать логику фильтрации пациентов по привязке к врачу
            // В реальном приложении нужно использовать APIGetDoctorPatients
            filtered = patients.filter(patient => 
                patient.assigned_doctor_id === currentDoctor.id
            );
        }
        
        setFilteredPatients(filtered);
    };
    
    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };
    
    const handleSubmit = async () => {
        if (!formData.test_id || !formData.patient_id) {
            setError("Выберите тест и пациента");
            return;
        }
        
        try {
            setSaving(true);
            setError(null);
            setSuccess(null);
            
            const response = await APIAssignTest(token, formData);
            
            if (response.success) {
                setSuccess("Тест успешно назначен");
                setTimeout(() => {
                    setModalAssignTest(false);
                }, 1500);
            } else {
                setError(response.error || "Ошибка при назначении теста");
            }
        } catch (error) {
            console.error("Ошибка при назначении теста:", error);
            setError("Ошибка при назначении теста");
        } finally {
            setSaving(false);
        }
    };
    
    return (
        <AnimatePresence>
            {modalAssignTest && (
                <Modal
                    sx={{ zIndex: 10009 }}
                    open={modalAssignTest}
                    slotProps={{
                        backdrop: {
                            component: motion.div,
                            initial: { opacity: 0, backdropFilter: 'blur(0px)' },
                            animate: { opacity: 1, backdropFilter: 'blur(8px)' },
                            exit: { opacity: 0, backdropFilter: 'blur(0px)' },
                            transition: { duration: 0.3, ease: "easeInOut" },
                        }
                    }}
                >
                    <ModalOverflow sx={{ height: 'auto' }}>
                        <ModalDialog
                            size="md"
                            component={motion.div}
                            initial={{ opacity: 0, y: -10, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.98 }}
                            transition={{ duration: 0.2, ease: "easeInOut" }}
                            sx={(theme) => ({
                                [theme.breakpoints.down("sm")]: { width: '95%', p: 3 },
                                width: '100%'
                            })}
                        >
                            <ModalClose onClick={() => setModalAssignTest(false)} />
                            
                            <Typography sx={{ textAlign: 'center', mb: 2 }} level="h3">
                                Назначить тест
                            </Typography>
                            
                            {error && (
                                <Alert color="danger" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}
                            
                            {success && (
                                <Alert color="success" sx={{ mb: 2 }}>
                                    {success}
                                </Alert>
                            )}
                            
                            {loading ? (
                                <Stack alignItems="center" justifyContent="center" p={4}>
                                    <CircularProgress />
                                    <Typography sx={{ mt: 2 }}>Загрузка данных...</Typography>
                                </Stack>
                            ) : (
                                <Stack spacing={3}>
                                    {/* Выбор теста */}
                                    <FormControl required>
                                        <FormLabel>Тест</FormLabel>
                                        <Select
                                            slotProps={{ listbox: { sx: { zIndex: 10010 } } }}
                                            placeholder="Выберите тест"
                                            value={formData.test_id}
                                            onChange={(e, value) => handleChange('test_id', value)}
                                            startDecorator={<AssignmentRounded />}
                                        >
                                            {tests.map(test => (
                                                <Option key={test.id} value={test.id}>
                                                    {test.name}
                                                    <Typography level="body-xs" color="neutral">
                                                        {test.test_type === 'psychology' ? 'Психологический' :
                                                         test.test_type === 'diagnostic' ? 'Диагностический' :
                                                         test.test_type === 'screening' ? 'Скрининговый' : 'Самооценка'}
                                                    </Typography>
                                                </Option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    
                                    {/* Выбор пациента */}
                                    <FormControl required>
                                        <FormLabel>Пациент</FormLabel>
                                        <Select
                                            slotProps={{ listbox: { sx: { zIndex: 10010 } } }}
                                            placeholder="Выберите пациента"
                                            value={formData.patient_id}
                                            onChange={(e, value) => handleChange('patient_id', value)}
                                            startDecorator={<PersonRounded />}
                                        >
                                            {filteredPatients.map(patient => (
                                                <Option key={patient.id} value={patient.id}>
                                                    {patient.name}
                                                    {patient.phone && (
                                                        <Typography level="body-xs" color="neutral">
                                                            {patient.phone}
                                                        </Typography>
                                                    )}
                                                </Option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    
                                    {/* Срок выполнения */}
                                    <FormControl>
                                        <FormLabel>Срок выполнения (опционально)</FormLabel>
                                        <Input
                                            type="date"
                                            value={formData.deadline}
                                            onChange={(e) => handleChange('deadline', e.target.value)}
                                            startDecorator={<CalendarTodayRounded />}
                                        />
                                    </FormControl>
                                    
                                    {/* Комментарий */}
                                    <FormControl>
                                        <FormLabel>Комментарий для пациента</FormLabel>
                                        <Textarea
                                            minRows={3}
                                            placeholder="Введите комментарий или инструкции для пациента..."
                                            value={formData.comment}
                                            onChange={(e) => handleChange('comment', e.target.value)}
                                        />
                                    </FormControl>
                                    
                                    {/* Кнопки действий */}
                                    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                                        <Button
                                            variant="outlined"
                                            color="neutral"
                                            fullWidth
                                            onClick={() => setModalAssignTest(false)}
                                            disabled={saving}
                                        >
                                            Отмена
                                        </Button>
                                        <Button
                                            variant="solid"
                                            color="primary"
                                            fullWidth
                                            onClick={handleSubmit}
                                            loading={saving}
                                            disabled={!formData.test_id || !formData.patient_id}
                                        >
                                            Назначить
                                        </Button>
                                    </Stack>
                                </Stack>
                            )}
                        </ModalDialog>
                    </ModalOverflow>
                </Modal>
            )}
        </AnimatePresence>
    );
}